/**
 * EmployeeSelect — dropdown of employees with name / title / department.
 * Prefer this over free-text employee IDs in every submit form.
 */
import { Select } from '../../../shell/Field.js';
import { useEmployees, useEmployeeDirectory } from '../api.js';
import type { Employee } from '@shared/types';

export type EmployeeOption = Pick<Employee, 'id' | 'name' | 'title' | 'department'> & {
  employmentStatus?: Employee['employmentStatus'];
};

export function formatEmployeeOption(
  e: Pick<Employee, 'name' | 'title' | 'department'> & { legalName?: string | null },
): string {
  const legal = e.legalName?.trim();
  const label =
    legal && legal.toLowerCase() !== e.name.trim().toLowerCase()
      ? `${e.name} (${legal})`
      : e.name;
  const detail = [e.title, e.department].filter(Boolean).join(' · ');
  return detail ? `${label} — ${detail}` : label;
}

interface Props {
  label?: string;
  value: string;
  onChange: (employeeId: string) => void;
  /** Exclude these employee ids (e.g. self for separation of duties). */
  excludeIds?: string[];
  /** When true, use the all-roles directory endpoint instead of scoped list. */
  directory?: boolean;
  /** Only Active employees (default true). */
  activeOnly?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  required?: boolean;
}

export function EmployeeSelect({
  label = 'Employee',
  value,
  onChange,
  excludeIds = [],
  directory = false,
  activeOnly = true,
  disabled = false,
  error,
  placeholder = 'Select an employee…',
  required = false,
}: Props) {
  const scoped = useEmployees(undefined, { enabled: !directory });
  const dir = useEmployeeDirectory({ enabled: directory });

  const source = directory ? dir : scoped;
  const exclude = new Set(excludeIds);

  const options = ((source.data?.items ?? []) as EmployeeOption[])
    .filter((e) => !exclude.has(e.id))
    .filter((e) => {
      if (!activeOnly) return true;
      if (!e.employmentStatus) return true;
      return e.employmentStatus === 'Active';
    });

  return (
    <Select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || source.isLoading}
      error={error}
      required={required}
    >
      <option value="">{source.isLoading ? 'Loading…' : placeholder}</option>
      {options.map((e) => (
        <option key={e.id} value={e.id}>
          {formatEmployeeOption(e)}
        </option>
      ))}
    </Select>
  );
}
